/**
 * Живой стакан Polymarket с CLOB, пока открыта карточка.
 *
 * Gamma и CLOB отдают `*`, сервер не нужен (см. `polyBook.ts`). Кадр ленты —
 * запас: если запрос не дошёл, лестница остаётся той, что пришла в вилке.
 */

import { PRIVATE_MIRROR, RU_FEED } from '../lib/feedContext';
import { useCupisLivePolyBook } from './useCupisLivePolyBook';
import { Decimal } from 'decimal.js';
import { useEffect, useState } from 'react';

import type { BookLevel } from '../lib/calc';
import { liveAsksOf, rememberLiveAsks, useLiveBookTick } from './liveBook';
import { bookOf, tokensOf } from './polyBook';

const POLL_MS = 2000;

export interface LivePolyBook {
  asks: readonly BookLevel[] | null;
  live: boolean;
  at: number | null;
  /** Токен нашли, CLOB ответил пустой книгой — рынок закрыт или без глубины. */
  empty: boolean;
}

function sortAsks(asks: readonly BookLevel[]): BookLevel[] {
  return [...asks].sort((a, b) => new Decimal(a[0]).cmp(new Decimal(b[0])));
}

function useLegacyLivePolyBook(
  leg: {
    url: string | null;
    asset: string | null;
    book?: readonly (readonly [string, string])[] | null;
    marketKey?: string | null;
    side?: string | null;
    name?: string | null;
    who?: string[];
  } | null,
): LivePolyBook {
  const [fresh, setFresh] = useState<{ asks: BookLevel[]; at: number } | null>(null);
  const [empty, setEmpty] = useState(false);
  const url = leg?.url ?? null;
  const asset = leg?.asset ?? null;
  const marketKey = leg?.marketKey ?? null;
  const side = leg?.side ?? null;
  const want = leg?.name ?? null;
  const query = (leg?.who ?? []).map((name) => name.trim()).filter(Boolean).join(' ') || null;
  useLiveBookTick();
  useEffect(() => {
    if (url === null && asset === null && (want === null || want === '') && query === null) {
      setFresh(null);
      setEmpty(false);
      return;
    }
    let gone = false;
    const pull = async (): Promise<void> => {
      const tokens = await tokensOf({ url, asset, marketKey, side, want, query });
      if (gone) return;
      if (tokens === null) return;
      const book = await bookOf(tokens.mine);
      if (gone) return;
      if (book === null || book.asks.length === 0) {
        setEmpty(true);
        return;
      }
      setEmpty(false);
      const asks = sortAsks(book.asks);
      const at = Date.now();
      rememberLiveAsks({ url, asset }, asks, at);
      setFresh((prev) => {
        if (
          prev
          && prev.asks.length === asks.length
          && prev.asks.every((row, i) => row[0] === asks[i]![0] && row[1] === asks[i]![1])
        ) {
          return prev;
        }
        return { asks, at };
      });
    };
    void pull();
    const timer = setInterval(() => void pull(), POLL_MS);
    return () => {
      gone = true;
      clearInterval(timer);
    };
  }, [url, asset, marketKey, side, want, query]);
  const stored = liveAsksOf({ url, asset });
  const tape = leg?.book;
  const feed = tape != null && tape.length > 0 ? sortAsks([...tape]) : null;
  return {
    asks: fresh?.asks ?? stored ?? feed,
    live: fresh !== null || stored !== null,
    at: fresh?.at ?? null,
    empty,
  };
}

/** Ruble feeds share exact outcome identity and fresh-book lifecycle. */
export const useLivePolyBook = (PRIVATE_MIRROR || RU_FEED) ? useCupisLivePolyBook : useLegacyLivePolyBook;
